import { CombinedDownload } from '@goldcast/api/content';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { doesClipHavePlayerConfig } from '@goldcast/player-config/src/utils/clipUtils';
import DownloadsPopupV2Layout from './DownloadsPopupV2Layout';
import { useBrandingToggleVisible } from '../SharePopup/useBrandingToggleVisible';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import useDownloadAsset from '@/hooks/useDownloadAsset';
import useTranslationState from '@/hooks/useTranslationState';
import useClipCompositionStatus from '@/hooks/useClipCompositionStatus';
import { useClipPendingUpdate } from '@/hooks/useClipPendingUpdate';
import { ClipSyncStatus } from '@/stores/pendingEditsStore';
import { ApiButtonStatus } from '@/components/atoms/ApiButton/ApiButtonTypes';
import { onLanguageChange } from '@/stores/clip';

interface ClipDownloadPopupProps {
  loading: boolean;
  processingDownloads: CombinedDownload[];
  recentDownloads: CombinedDownload[];
}

export default function ClipDownloadPopup({
  loading,
  processingDownloads,
  recentDownloads
}: Readonly<ClipDownloadPopupProps>) {
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [buttonStatus, setButtonStatus] = useState<ApiButtonStatus>(ApiButtonStatus.IDLE);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const { clipData } = useClipsContext();
  const { hasMultipleTranslatedLanguages, translatedLanguagesOptionsWithoutAction } = useTranslationState(clipData);

  const { isDownloadProcessed, onDownloadClip, updateDownloadState } = useDownloadAsset({
    type: 'CLIP',
    clip: clipData
  });

  // Keep ref updated with latest updateDownloadState
  const updateDownloadStateRef = useRef(updateDownloadState);
  useEffect(() => {
    updateDownloadStateRef.current = updateDownloadState;
  }, [updateDownloadState]);

  const { showClipCompositionLoader } = useClipCompositionStatus();
  const { status: clipStatus } = useClipPendingUpdate({ clipId: clipData.id });

  const isRecordingsPage = clipData.asset_metadata.is_full_length;

  const hasPlayerConfig = useMemo(() => {
    return doesClipHavePlayerConfig(clipData);
  }, [clipData]);

  const shouldShowLanguageSelection = useMemo(() => {
    return !isRecordingsPage && hasMultipleTranslatedLanguages;
  }, [isRecordingsPage, hasMultipleTranslatedLanguages]);

  const isDownloadDisabled = useMemo(() => {
    const isConfigMissing = !hasPlayerConfig;
    const isClipNotReady = isConfigMissing || showClipCompositionLoader || clipStatus !== ClipSyncStatus.sync_completed;
    const isDownloadInProgress = buttonStatus === ApiButtonStatus.IN_PROGRESS;

    return isClipNotReady || isDownloadInProgress;
  }, [showClipCompositionLoader, clipStatus, hasPlayerConfig, buttonStatus]);

  const statusMessage = useMemo(() => {
    if (clipStatus === ClipSyncStatus.sync_failed) {
      return 'Changes not saved. Please try again.';
    }
    if (clipStatus === ClipSyncStatus.sync_in_progress) {
      return 'Saving changes...';
    }
    if (showClipCompositionLoader) {
      return 'Please wait...';
    }

    if (buttonStatus === ApiButtonStatus.IN_PROGRESS) {
      return 'Initiating Download..';
    }
    if (buttonStatus === ApiButtonStatus.COMPLETED && isDownloadProcessed) {
      return 'Download started';
    }
    if (buttonStatus === ApiButtonStatus.FAILED) {
      return 'Download failed';
    }
    return null;
  }, [clipStatus, showClipCompositionLoader, buttonStatus, isDownloadProcessed]);

  const handleStatusChange = useCallback((status: ApiButtonStatus) => {
    setButtonStatus(status);
    updateDownloadStateRef.current(status);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
    };

    if (showLanguageDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showLanguageDropdown]);

  const handleDownloadClick = useCallback(async (): Promise<void> => {
    if (shouldShowLanguageSelection) {
      setShowLanguageDropdown(prev => !prev);
      return;
    }

    handleStatusChange(ApiButtonStatus.IN_PROGRESS);
    try {
      await onDownloadClip();
      handleStatusChange(ApiButtonStatus.COMPLETED);
    } catch {
      handleStatusChange(ApiButtonStatus.FAILED);
    }
  }, [shouldShowLanguageSelection, onDownloadClip, handleStatusChange]);

  const handleLanguageSelect = useCallback(
    async (languageName: string) => {
      const language = translatedLanguagesOptionsWithoutAction.find(lang => lang.name === languageName);

      if (!language) {
        return;
      }

      const isAlreadySelected = clipData.asset_metadata?.selected_lang === language.code;

      setShowLanguageDropdown(false);

      handleStatusChange(ApiButtonStatus.IN_PROGRESS);

      try {
        if (isAlreadySelected) {
          await onDownloadClip();
        } else {
          await onLanguageChange(clipData.id, language, async () => {
            await onDownloadClip();
          });
        }
        handleStatusChange(ApiButtonStatus.COMPLETED);
      } catch (error) {
        console.error('Failed to download clip:', error);
        handleStatusChange(ApiButtonStatus.FAILED);
      }
    },
    [
      translatedLanguagesOptionsWithoutAction,
      clipData.asset_metadata?.selected_lang,
      clipData.id,
      onDownloadClip,
      handleStatusChange
    ]
  );

  const languageDropdown =
    showLanguageDropdown && shouldShowLanguageSelection ? (
      <div
        ref={languageDropdownRef}
        className="absolute right-4 top-full z-10 mt-1 w-48 rounded-md border bg-white shadow-lg"
      >
        <div className="px-3 py-2 text-xs font-medium text-gray-500">Languages</div>
        <div className="max-h-60 overflow-y-auto">
          {translatedLanguagesOptionsWithoutAction.map(option => (
            <button
              key={option.code}
              onClick={() => handleLanguageSelect(option.name)}
              className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-gray-100"
            >
              <span className="flex-1">
                {option.name}
                {option.subText && <span className="ml-1 text-gray-500">{option.subText}</span>}
              </span>
            </button>
          ))}
        </div>
      </div>
    ) : null;

  const showBrandingToggle = useBrandingToggleVisible();

  return (
    <DownloadsPopupV2Layout
      loading={loading}
      processingDownloads={processingDownloads}
      recentDownloads={recentDownloads}
      downloadAction={handleDownloadClick}
      statusMessage={statusMessage}
      isDownloadDisabled={isDownloadDisabled}
      languageDropdown={languageDropdown}
      showBrandingToggle={showBrandingToggle}
    />
  );
}
